import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Gift, Users, ArrowRight, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Compact viral referral teaser card for all role dashboards.
 * Shows user's code + invite CTA in a single row. Dismissible per-session.
 */
const InviteTeaserCard = () => {
  const [stats, setStats] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/viral/me`);
      setStats(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('invite-teaser-dismissed') === '1') {
      setDismissed(true);
      return;
    }
    load();
  }, [load]);

  if (dismissed) return null;
  // While stats are loading, render an invisible placeholder of the same height
  // so the dashboard below doesn't jump when the real card arrives.
  if (!stats) {
    return <div className="h-[72px] mb-6" aria-hidden="true" data-testid="invite-teaser-placeholder" />;
  }

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem('invite-teaser-dismissed', '1');
    setDismissed(true);
  };

  const remaining = stats.next_reward?.remaining ?? 0;
  const nextLabel = stats.next_reward?.label;

  return (
    <Link
      to="/invite"
      className="relative block bg-gradient-to-r from-[#0F172A] to-[#1e293b] text-white px-5 py-4 border border-[#E07A3A]/30 hover:border-[#E07A3A] transition-colors mb-6 group"
      data-testid="invite-teaser-card"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-white/30 hover:text-white/80 transition-colors"
        aria-label="Dismiss"
        data-testid="invite-teaser-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="flex items-center gap-4 pr-6">
        <div className="w-10 h-10 bg-[#E07A3A]/20 border border-[#E07A3A]/40 flex items-center justify-center flex-shrink-0">
          <Gift className="h-5 w-5 text-[#E07A3A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">
            Level {stats.level || 1} Ambassador · Invite & Unlock
          </p>
          <p className="text-sm font-semibold truncate">
            {stats.referrals_count === 0
              ? 'Share Shopperden — unlock your first perk'
              : nextLabel
                ? `${remaining} more invites to unlock ${nextLabel}`
                : 'You are a Legendary Ambassador! Keep building your network.'
            }
          </p>
          <p className="text-xs text-white/40 mt-0.5 flex items-center gap-2">
            <span className="font-mono text-[#E07A3A]">{stats.code}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {stats.referrals_count} invites · {stats.xp || 0} XP</span>
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-[#E07A3A] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

export default InviteTeaserCard;
